import { useQueryClient } from '@tanstack/react-query';
import { Button } from 'components/atoms';
import { DescriptiveInputWrapper, FormInput } from 'components/molecules';
import PopupForm from 'components/molecules/popup/form/PopupForm';
import PopupTwoFactor from 'components/molecules/popup/two-factor/PopupTwoFactor';

import { passwordRegExp } from 'constants/passwordRegExp';
import { FormikHelpers } from 'formik';
import { useMutateOptions } from 'hooks';
import i18next, { t } from 'i18next';
import { useStoreState } from 'providers/StoreStateContext';
import {
  PiFingerprintSimple,
  PiPower,
  PiQrCode,
  PiShieldCheck,
  PiShieldSlash,
} from 'react-icons/pi';

import {
  useConfirmTwoFactorOTP,
  useDisableTwoFactor,
  useOutsideAuthorization,
  useTwoFactor,
} from 'services/auth/twoFactor';

import { PopupActions } from 'reactjs-popup/dist/types';
import { useChangePassword } from 'services/auth/changePassword';
import { IChangePasswordForm } from 'services/auth/types';
import { TWO_FACTOR_STATUS_KEY, useGetTwoFactorStatus } from 'services/twoFactor';
import { BUTTON_VARIANT } from 'types/components/button/button.interface';
import { object, ref, string } from 'yup';
import DataWrapper from 'components/molecules/data-wrapper/DataWrapper';
import ViewHint from 'components/molecules/hint/ViewHint';

const newPasswordInitialValues: IChangePasswordForm = {
  currentPassword: '',
  newPassword: '',
  confirmPassword: '',
};

const newPasswordValidationSchema = object().shape({
  currentPassword: string().required(i18next.t('formik-error.required')),
  newPassword: string()
    .matches(passwordRegExp, i18next.t('formik-error.invalid-password'))
    .required(i18next.t('formik-error.required')),
  confirmPassword: string()
    .oneOf([ref('newPassword'), ''], i18next.t('formik-error.unmatch-password'))
    .required(i18next.t('formik-error.required')),
});

const SecuritySettings = () => {
  const queryClient = useQueryClient();
  const { clearTokens } = useStoreState();
  const changePassword = useChangePassword();
  const mutateOptions = useMutateOptions<any>();

  const enableTwoFactor = useTwoFactor();
  const disableTwoFactor = useDisableTwoFactor();
  const enableOutsideAuthorization = useOutsideAuthorization();
  const confirmTwoFactorOTP = useConfirmTwoFactorOTP();

  const { tokenData } = useStoreState();

  const twoFactorStatus = useGetTwoFactorStatus(tokenData?.UserId, true);

  const invalidateTwoFactorStatus = () =>
    queryClient.invalidateQueries({
      queryKey: TWO_FACTOR_STATUS_KEY(tokenData?.UserId),
    });

  const isTwoFactorEnabled = !!twoFactorStatus.data?.isTwoFactorEnabled;
  const isOtpEnabled = !!twoFactorStatus.data?.isOtpEnabled;

  const onEnableTwoFactor = async () => {
    await enableTwoFactor.mutateAsync(undefined as any, {
      onSuccess: () => invalidateTwoFactorStatus(),
    });
  };

  const onDisableTwoFactor = async () => {
    await disableTwoFactor.mutateAsync(undefined as any, {
      onSuccess: () => invalidateTwoFactorStatus(),
    });
  };

  const onChangePassword = async (
    values: IChangePasswordForm,
    formikHelpers: FormikHelpers<IChangePasswordForm>,
    actions: PopupActions | null,
  ) =>
    await changePassword.mutateAsync(values, {
      ...mutateOptions(formikHelpers),
      onSuccess: () => {
        actions?.close();
        formikHelpers.resetForm();
      },
    });

  return (
    <DataWrapper>
      <div />
      <ViewHint hintId="two-factor-authentication" />
      {!isTwoFactorEnabled ? (
        <Button
          fullWidth
          variant={BUTTON_VARIANT.white}
          onClick={onEnableTwoFactor}
          isLoading={enableTwoFactor.isPending}
        >
          <Button.Icon icon={<PiShieldCheck />} />
          <Button.Text text={t('button.enable')} />
        </Button>
      ) : (
        <Button
          variant={BUTTON_VARIANT.remove}
          fullWidth
          onClick={onDisableTwoFactor}
          isLoading={disableTwoFactor.isPending}
        >
          <Button.Icon icon={<PiShieldSlash />} />
          <Button.Text text={t('button.disable')} />
        </Button>
      )}

      {isTwoFactorEnabled && (
        <PopupTwoFactor
          mode="qr+otp"
          trigger={
            <Button
              disabled={enableOutsideAuthorization.isPending}
              fullWidth
              style={isOtpEnabled ? { pointerEvents: 'none' } : {}}
              variant={BUTTON_VARIANT.white}
            >
              <Button.Icon icon={isOtpEnabled ? <PiShieldCheck /> : <PiQrCode />} />
              <Button.Text
                text={
                  isOtpEnabled
                    ? t('button.outsideAuthorizationOn')
                    : t('button.outsideAuthorizationOff')
                }
              />
            </Button>
          }
          header={
            <>
              <PiQrCode /> {t('setting.user.outsideAuthorization')}
            </>
          }
          fetchQr={() =>
            enableOutsideAuthorization.mutateAsync(undefined as any, {
              onSuccess: () => invalidateTwoFactorStatus(),
            })
          }
          submitOtp={(code, popup) =>
            confirmTwoFactorOTP.mutateAsync(code, {
              ...mutateOptions({} as any),
              onSuccess: () => {
                invalidateTwoFactorStatus();
                popup?.close();
              },
            })
          }
          submitLabelKey="button.confirm"
        >
          <FormInput.Text appBackground name="code" label={t('input.label.code')} type="string" />
        </PopupTwoFactor>
      )}

      <div />

      <ViewHint hintId="security" />
      <PopupForm
        initialValues={newPasswordInitialValues}
        onSubmit={onChangePassword}
        validationSchema={newPasswordValidationSchema}
        submitButtonText={t('button.change')}
        header={
          <>
            <PiFingerprintSimple /> {t('reset-password.button')}
          </>
        }
        trigger={
          <Button fullWidth variant={BUTTON_VARIANT.white}>
            <Button.Icon icon={<PiFingerprintSimple />} />
            <Button.Text text={t('reset-password.button')} />
          </Button>
        }
      >
        <FormInput.Text
          name="currentPassword"
          type="password"
          label={t('input.label.current-password')}
          appBackground
        />
        <div />
        <FormInput.Text
          name="newPassword"
          type="password"
          label={t('input.label.new-password')}
          appBackground
        />
        <FormInput.Text
          name="confirmPassword"
          type="password"
          label={t('input.label.confirm-password')}
          appBackground
        />
      </PopupForm>
      <Button
        fullWidth
        variant={BUTTON_VARIANT.white}
        onClick={() => {
          clearTokens();
          queryClient.removeQueries();
        }}
      >
        <Button.Icon icon={<PiPower />} />
        <Button.Text text={t('navbar.logout')} />
      </Button>

      {/* <DescriptiveInputWrapper
        header={t('setting.user.logout')}
        description={t('setting.user.logoutDescription')}>
        <Button
          fullWidth
          variant={BUTTON_VARIANT.white}
          onClick={() => {
            clearTokens();
            queryClient.removeQueries();
          }}>
          <Button.Icon icon={<PiPower />} />
          <Button.Text text={t('navbar.logout')} />
        </Button>
      </DescriptiveInputWrapper> */}
    </DataWrapper>
  );
};

export default SecuritySettings;
